/**
 * Pagina publica de bootstrap de passkey via convite.
 *
 * URL: /register-passkey?token=<inviteToken>
 *
 * Fluxo:
 *  1. Le token da querystring.
 *  2. Sugere um nickname baseado no user-agent.
 *  3. No submit chama registerPasskey — que faz options + prompt do OS + verify.
 *  4. Backend devolve token/user quando fluxo de convite: aplicamos sessao
 *     via AuthContext.applySession e redirecionamos pra /dashboard.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  ThemeToggle,
} from "@payflow/ui";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSelector } from "../components/LanguageSelector";
import {
  isPasskeySupported,
  registerPasskey,
  suggestPasskeyNickname,
} from "../services/webauthn.service";

export function RegisterPasskeyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { applySession, isAuthenticated } = useAuth();

  const token = searchParams.get("token");
  const supported = isPasskeySupported();

  const defaultNickname = useMemo(() => suggestPasskeyNickname(), []);
  const [nickname, setNickname] = useState(defaultNickname);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Se ja estiver logado, oferece caminho direto pro settings.
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard/chaves", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) return;
    if (!nickname.trim()) {
      setError(t("passkeys.error_nickname_required"));
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await registerPasskey(nickname.trim(), token);
      if (result.token && result.user) {
        applySession(result.token, result.user, result.tenant);
        navigate("/dashboard", { replace: true });
      } else {
        // TODO(review): backend deveria sempre devolver sessao no fluxo de convite.
        // Se nao devolver, mandamos pro login pra completar autenticacao.
        navigate("/login", { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setError(mapError(message, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-primary">
            <span className="text-3xl font-bold text-white">P</span>
          </div>
          <CardTitle className="text-2xl">
            {t("auth.passkey.register_title")}
          </CardTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("auth.passkey.register_subtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {!token ? (
            <InviteInvalid t={t} />
          ) : !supported ? (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
              <p className="font-semibold text-warning">
                {t("auth.passkey.unsupported_title")}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("auth.passkey.unsupported_message")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t("passkeys.nickname_label")}
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value);
                  if (error) setError("");
                }}
                placeholder={t("passkeys.nickname_placeholder")}
                required
                autoFocus
              />

              {error && <p className="text-sm text-error">{error}</p>}

              <Button type="submit" className="w-full" loading={loading}>
                {t("auth.passkey.register_submit")}
              </Button>

              <p className="pt-2 text-center text-xs text-muted-foreground">
                {t("auth.passkey.register_helper")}
              </p>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <Link to="/" className="text-primary-600 hover:underline">
              {t("auth.back_home")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InviteInvalid({ t }: { t: (key: string) => string }) {
  return (
    <div className="rounded-lg border border-error/40 bg-error/10 p-4 text-sm">
      <p className="font-semibold text-error">
        {t("auth.passkey.invite_invalid_title")}
      </p>
      <p className="mt-1 text-muted-foreground">
        {t("auth.passkey.invite_invalid_message")}
      </p>
    </div>
  );
}

function mapError(code: string, t: (key: string) => string): string {
  switch (code) {
    case "PASSKEY_CANCELED":
      return t("auth.passkey.error_canceled");
    case "PASSKEY_UNSUPPORTED":
      return t("auth.passkey.error_unsupported");
    case "PASSKEY_ALREADY_REGISTERED":
      return t("auth.passkey.error_already_registered");
    default:
      return code || t("auth.passkey.error_generic");
  }
}
